import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell,
  ExternalLink,
  Mail,
  MessageCircle,
  MessageSquareText,
  Star,
  TriangleAlert,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HealthBadge } from "@/components/pages/communication-integrations/components/HealthBadge";
import {
  CHANNEL_TO_INTEGRATION_KIND,
  CommunicationChannelKind,
  MessagingChannelConfig,
} from "@/types/messaging-channel-config";
import {
  extractMissingIntegrationError,
  MissingIntegrationError,
} from "../utils/missing-integration-error";
import {
  useDisableMessagingChannel,
  useEnableMessagingChannel,
  useSetDefaultMessagingChannel,
} from "../hooks/messaging-channel-configs.hooks";
import { DisableChannelDialog } from "./DisableChannelDialog";

interface ChannelCardProps {
  hotelId: string;
  channel: CommunicationChannelKind;
  config: MessagingChannelConfig | null;
}

const CHANNEL_ICON: Record<CommunicationChannelKind, React.ReactNode> = {
  IN_APP: <Bell className="size-5 text-skyline-2" />,
  EMAIL: <Mail className="size-5 text-skyline-2" />,
  SMS: <MessageSquareText className="size-5 text-skyline-2" />,
  WHATSAPP: <MessageCircle className="size-5 text-skyline-2" />,
};

export const ChannelCard: React.FC<ChannelCardProps> = ({
  hotelId,
  channel,
  config,
}) => {
  const { t } = useTranslation();
  const [disableOpen, setDisableOpen] = React.useState(false);
  const [enableBlocked, setEnableBlocked] =
    React.useState<MissingIntegrationError | null>(null);

  const { mutate: enableChannel, isPending: isEnabling } =
    useEnableMessagingChannel(hotelId);
  const { mutate: disableChannel, isPending: isDisabling } =
    useDisableMessagingChannel(hotelId);
  const { mutate: setDefault, isPending: isSettingDefault } =
    useSetDefaultMessagingChannel(hotelId);

  const isConfigured = config !== null;
  const isEnabled = config?.enabled ?? false;
  const isDefault = config?.isDefault ?? false;
  const isInApp = channel === "IN_APP";
  const isToggling = isEnabling || isDisabling;
  const linkedIntegration = !isInApp
    ? CHANNEL_TO_INTEGRATION_KIND[channel]
    : null;

  React.useEffect(() => {
    if (!isConfigured) return;
    if (config?.integrationReady) setEnableBlocked(null);
  }, [config?.updatedAt, config?.integrationReady, isConfigured]);

  const switchGated =
    isConfigured &&
    !isInApp &&
    !isEnabled &&
    (!config!.integrationReady || !config!.providerAvailable);

  const handleSwitchChange = (next: boolean) => {
    setEnableBlocked(null);
    if (next) {
      enableChannel(channel, {
        onSuccess: () => {
          toast.success(t("communicationChannels.matrix.enable.success"));
        },
        onError: (error: unknown) => {
          const blocked = extractMissingIntegrationError(error);
          if (blocked) {
            setEnableBlocked({ ...blocked, channel });
            toast.error(
              t("communicationChannels.matrix.enable.errorIntegration", {
                kind: t(
                  `communicationChannels.kinds.${blocked.missingIntegration}.label`,
                ),
              }),
            );
          } else {
            toast.error(t("communicationChannels.matrix.enable.error"));
          }
        },
      });
    } else {
      setDisableOpen(true);
    }
  };

  const handleSetDefault = () => {
    setDefault(channel, {
      onSuccess: () =>
        toast.success(t("communicationChannels.matrix.setDefault.success")),
      onError: () =>
        toast.error(t("communicationChannels.matrix.setDefault.error")),
    });
  };

  const handleConfirmDisable = () => {
    disableChannel(channel, {
      onSuccess: () => {
        toast.success(t("communicationChannels.matrix.disable.success"));
        setDisableOpen(false);
      },
      onError: () => {
        toast.error(t("communicationChannels.matrix.disable.error"));
      },
    });
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {CHANNEL_ICON[channel]}
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-base font-semibold text-foreground leading-tight">
                {t(`communicationChannels.channels.${channel}.label`)}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {renderStateBadge(isConfigured, isEnabled, t)}
                {isDefault && (
                  <Badge variant="verified">
                    <Star className="size-3" aria-hidden />
                    {t("communicationChannels.matrix.defaultBadge")}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isConfigured && isEnabled && !isDefault && (
              <Button
                type="button"
                variant="new_secondary"
                size="sm"
                disabled={isSettingDefault}
                onClick={handleSetDefault}
              >
                <Star className="size-4" />
                {t("communicationChannels.matrix.actions.setAsDefault")}
              </Button>
            )}

            {switchGated ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Switch
                        checked={false}
                        disabled
                        aria-label={t(
                          "communicationChannels.matrix.actions.toggleAria",
                        )}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-xs">
                    {linkedIntegration &&
                      t(
                        "communicationChannels.matrix.tooltip.integrationNotReady",
                        {
                          kind: t(
                            `communicationChannels.kinds.${linkedIntegration}.label`,
                          ),
                        },
                      )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Switch
                checked={isEnabled}
                disabled={isToggling}
                aria-label={t(
                  "communicationChannels.matrix.actions.toggleAria",
                )}
                onCheckedChange={handleSwitchChange}
              />
            )}
          </div>
        </div>

        {isConfigured && config && !isInApp && (
          <div className="flex flex-col gap-2 border-t pt-4">
            <div className="flex flex-wrap items-center gap-3">
              {config.providerAvailable ? (
                <Badge variant="success">
                  {t("communicationChannels.matrix.provider.available")}
                </Badge>
              ) : (
                <Badge variant="new_secondary">
                  {t("communicationChannels.matrix.provider.comingSoon")}
                </Badge>
              )}

              <IntegrationBadge isReady={config.integrationReady} />

              {config.integrationReady && config.integrationHealth && (
                <HealthBadge status={config.integrationHealth} />
              )}
            </div>

            {channel === "EMAIL" && config.lastInboundError && (
              <div className="flex items-start gap-2 text-xs text-destructive">
                <TriangleAlert className="size-3.5 shrink-0 mt-0.5" />
                <p className="line-clamp-2" title={config.lastInboundError}>
                  {t("communicationChannels.matrix.inboundError", {
                    error: config.lastInboundError,
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        {enableBlocked && (
          <div
            className="flex flex-col gap-1 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            role="alert"
          >
            <p>
              {enableBlocked.missingField
                ? t("communicationChannels.matrix.enable.blockedWithField", {
                    kind: t(
                      `communicationChannels.kinds.${enableBlocked.missingIntegration}.label`,
                    ),
                    field: enableBlocked.missingField,
                  })
                : t("communicationChannels.matrix.enable.blocked", {
                    kind: t(
                      `communicationChannels.kinds.${enableBlocked.missingIntegration}.label`,
                    ),
                  })}
            </p>
            <Link
              to="/communication-integrations"
              className="inline-flex items-center gap-1 text-skyline-2 hover:underline w-fit"
            >
              {t("communicationChannels.matrix.enable.openIntegrationLink", {
                kind: t(
                  `communicationChannels.kinds.${enableBlocked.missingIntegration}.label`,
                ),
              })}
              <ExternalLink className="size-3" />
            </Link>
          </div>
        )}

        {!isConfigured && (
          <p className="text-sm text-muted-foreground">
            {t(
              `communicationChannels.channels.${channel}.placeholderDescription`,
            )}
          </p>
        )}
      </CardContent>

      {isConfigured && (
        <DisableChannelDialog
          open={disableOpen}
          onOpenChange={setDisableOpen}
          channel={channel}
          isDefault={isDefault}
          isPending={isDisabling}
          onConfirm={handleConfirmDisable}
        />
      )}
    </Card>
  );
};

const IntegrationBadge: React.FC<{ isReady: boolean }> = ({ isReady }) => {
  const { t } = useTranslation();
  if (isReady) {
    return (
      <Badge variant="success">
        {t("communicationChannels.matrix.integration.ready")}
      </Badge>
    );
  }
  return (
    <Link to="/communication-integrations" className="inline-flex">
      <Badge variant="new_destructive">
        {t("communicationChannels.matrix.integration.notReady")}
        <ExternalLink className="size-3" />
      </Badge>
    </Link>
  );
};

const renderStateBadge = (
  isConfigured: boolean,
  isEnabled: boolean,
  t: (key: string) => string,
): React.ReactNode => {
  if (!isConfigured) {
    return (
      <Badge variant="new_secondary">
        {t("communicationChannels.status.notConfigured")}
      </Badge>
    );
  }
  if (isEnabled) {
    return (
      <Badge variant="success">
        {t("communicationChannels.status.enabled")}
      </Badge>
    );
  }
  return (
    <Badge variant="new_secondary">
      {t("communicationChannels.status.disabled")}
    </Badge>
  );
};
